import { UseFormReturn, useFieldArray } from 'react-hook-form';

import { Flex } from 'components/layout';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { QuoteChanges } from 'types/suggestions/QuoteSuggestions';

import { LTLAccessorial, QuickQuoteInputs } from '../../types';
import { AccessorialsSection } from './AccessorialsSection';
import { CommoditySection } from './CommoditySection';
import { ItemsSection } from './ItemsSection';
import { QuoteInformationSection } from './QuoteInformationSection';
import { StopsSection } from './StopsSection';
import { ValueSection } from './ValueSection';

export interface UnifiedTaiLTLFormProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  hasAddedQuoteDates: boolean;
  accessorials: LTLAccessorial[];
  isLoadingAccessorials?: boolean;
  integrationName?: TMS;
  // Batch LTL picks the customer once for every shipment, so its cards hide this section.
  showQuoteInformation?: boolean;
  // Tighter spacing for narrow containers such as a Batch LTL shipment card.
  isCompact?: boolean;
  // AI-filled hints for the stops; defaults to the clicked Kit suggestion.
  suggestedFields?: Maybe<QuoteChanges>;
}

export function UnifiedTaiLTLForm({
  formMethods,
  hasAddedQuoteDates,
  accessorials,
  isLoadingAccessorials = false,
  integrationName,
  showQuoteInformation = true,
  isCompact = false,
  suggestedFields,
}: UnifiedTaiLTLFormProps) {
  const itemFieldArray = useFieldArray({
    control: formMethods.control,
    name: 'ltl.items',
    rules: {
      required: 'At least one item is required',
    },
  });

  return (
    <Flex direction='col' gap={isCompact ? 'lg' : 'xl'} className='w-full'>
      {showQuoteInformation && (
        <QuoteInformationSection formMethods={formMethods} />
      )}
      <StopsSection
        formMethods={formMethods}
        hasAddedQuoteDates={hasAddedQuoteDates}
        integrationName={integrationName}
        accessorials={accessorials}
        suggestedFields={suggestedFields}
      />
      <CommoditySection
        formMethods={formMethods}
        integrationName={integrationName}
        replaceItems={itemFieldArray.replace}
      />
      <ItemsSection formMethods={formMethods} itemFieldArray={itemFieldArray} />
      <ValueSection />
      <AccessorialsSection
        formMethods={formMethods}
        accessorials={accessorials}
        isLoading={isLoadingAccessorials}
      />
    </Flex>
  );
}
